/**
 * CCTV Player — 실시간 교통 카메라 재생 카드.
 *
 * 소스마다 프레임 형태가 다르다:
 *   · hlsUrl  (Caltrans, 한국 ITS) → hls.js 실시간 스트림
 *   · videoUrl(TfL)                → 짧은 mp4 클립을 루프 + 주기 재취득
 *   · imageUrl(DriveBC/Ontario/NSW)→ 정지 프레임을 refreshSec 주기로 갱신
 * 프레임은 브라우저가 직접 로드한다 (CORS 불필요).
 */
import { type FC, useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { useAppStore } from '@/stores/app-store';
import { sensorAccent } from '@/components/map/sensor-optics';
import { cameraAttribution } from '@/services/traffic-cameras';

/** 캐시를 우회해 최신 프레임을 받기 위한 버스터 */
function bust(url: string, tick: number): string {
  return `${url}${url.includes('?') ? '&' : '?'}_t=${tick}`;
}

export const CctvPlayer: FC = () => {
  const selectedId  = useAppStore((s) => s.selectedCctvId);
  const setSelected = useAppStore((s) => s.setSelectedCctvId);
  const cameras     = useAppStore((s) => s.trafficCameras);
  const sensorMode  = useAppStore((s) => s.sensorMode);
  const accent = sensorAccent(sensorMode);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(() => Date.now());

  const cam = cameras.find((c) => c.id === selectedId);
  const hls = cam?.hlsUrl;
  const mp4 = cam?.videoUrl;
  const img = cam?.imageUrl;

  // ── 정지 프레임 / mp4 클립 주기 갱신 ──────────────────
  useEffect(() => {
    if (!cam || hls) return;
    const sec = Math.max(5, cam.refreshSec || 15);
    const id = setInterval(() => setTick(Date.now()), sec * 1000);
    return () => clearInterval(id);
  }, [cam, hls]);

  // ── HLS 스트림 ────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hls) return;
    setError(null);

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hls;
      video.play().catch(() => { /* autoplay 정책 */ });
      return;
    }
    if (!Hls.isSupported()) { setError('HLS not supported in this browser'); return; }

    const inst = new Hls({ manifestLoadingMaxRetry: 1, levelLoadingMaxRetry: 1, fragLoadingMaxRetry: 1 });
    inst.loadSource(hls);
    inst.attachMedia(video);
    inst.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); });
    inst.on(Hls.Events.ERROR, (_e, data) => {
      if (data.fatal) setError('Stream offline — this camera is not broadcasting right now');
    });
    return () => inst.destroy();
  }, [hls]);

  // ── mp4 클립: 새 tick 마다 다시 받아 루프 ──────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video || hls || !mp4) return;
    setError(null);
    video.src = bust(mp4, tick);
    video.play().catch(() => {});
  }, [mp4, hls, tick]);

  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, setSelected]);

  if (!selectedId || !cam) return null;

  const mode = hls ? 'LIVE STREAM' : mp4 ? 'LIVE CLIP' : `FRAME · ${cam.refreshSec}s`;

  return (
    <div className="fixed left-3 right-3 bottom-16 z-50 md:absolute md:left-2 md:right-auto md:w-[340px] md:bottom-24 pointer-events-auto">
      <div className="glass-panel hud-corners overflow-hidden" style={{ boxShadow: `inset 0 0 0 1px ${accent}55, 0 0 22px ${accent}18` }}>
        <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-surface-border">
          <div className="min-w-0">
            <div className="font-mono text-[11px] font-semibold truncate" style={{ color: accent }}>
              {cam.name}
            </div>
            <div className="font-mono text-[8px] text-slate-500 truncate">
              {cam.provider} · {cam.region} · {cam.lat.toFixed(3)}, {cam.lon.toFixed(3)}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 ml-2">
            <span className="font-mono text-[8px] tracking-[0.14em]" style={{ color: accent }}>{mode}</span>
            <button
              onClick={() => setSelected(null)}
              className="w-5 h-5 flex items-center justify-center rounded text-slate-500 hover:text-white hover:bg-slate-700 transition-colors text-[11px]"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="relative aspect-video bg-black">
          {hls || mp4 ? (
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              muted playsInline autoPlay loop={Boolean(mp4 && !hls)} controls={Boolean(hls)}
            />
          ) : img ? (
            <img
              key={tick}
              src={bust(img, tick)}
              alt={cam.name}
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setError('Frame unavailable')}
            />
          ) : null}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-950/85 px-4">
              <span className="font-mono text-[9px] text-threat-medium text-center leading-relaxed">{error}</span>
            </div>
          )}

          {/* 라이브 펄스 */}
          <div className="absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-black/55">
            <span className="w-1.5 h-1.5 rounded-full bg-threat-low animate-pulse" />
            <span className="font-mono text-[8px] text-slate-300">LIVE</span>
          </div>
        </div>

        {/* 라이선스상 귀속 표기가 요구되는 소스가 있다 */}
        <div className="px-2.5 py-1 border-t border-surface-border/60">
          <span className="font-mono text-[8px] text-slate-600 truncate block">{cameraAttribution(cam.provider)}</span>
        </div>
      </div>
    </div>
  );
};
